import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import type { SubscriptionView } from '../api/generated/models/SubscriptionView';
import { helper } from '../utils/helper';

/**
 * Hook to check if user has paid subscriptions for a specific plugin
 * @param pluginName - The plugin name to check (e.g., 'freshdesk', 'mobile')
 * @returns boolean - true if user has at least one active paid subscription for the plugin
 */
export const useLicenseCheck = (pluginName: string): boolean => {
  const { items: subscriptions } = useSelector((state: RootState) => state.licenses);

  return subscriptions.some((subscription: SubscriptionView) => {
    // Check if subscription is for the correct plugin
    if (subscription.pluginName !== pluginName) {
      return false;
    }

    // Check if subscription is active and not expired
    if (!helper.isSubscriptionActive(subscription)) {
      return false;
    }

    // All subscriptions from API are considered paid
    return true;
  });
};

/**
 * Hook to check if user has any valid subscription (free or paid) for a specific plugin
 * @param pluginName - The plugin name to check
 * @returns boolean - true if user has at least one active subscription for the plugin
 */
export const useAnyLicenseCheck = (pluginName: string): boolean => {
  const { items: subscriptions } = useSelector((state: RootState) => state.licenses);

  return subscriptions.some((subscription: SubscriptionView) => {
    return subscription.pluginName === pluginName && helper.isSubscriptionActive(subscription);
  });
};

/**
 * Product-option slugs that gate a feature. Each is resolved across ALL active subscriptions:
 * a feature is on when any active plan carries the option = 'true' (see config/products.json).
 * These slugs are unique per feature, so "any active plan" is the correct scope regardless of
 * which plugin grants them.
 */
const FEATURE_OPTIONS = {
  showLieferbar: 'shipping_orders_enabled',
  showDashboard: 'jtl_wawi_enabled',
  showTasks: 'tasks_enabled',
} as const;

export type AccountFeatures = { [K in keyof typeof FEATURE_OPTIONS]: boolean };

export function deriveAccountFeatures(subscriptions: SubscriptionView[]): AccountFeatures {
  const active = subscriptions.filter((s) => helper.isSubscriptionActive(s));
  const anyPlanHas = (slug: string): boolean =>
    active.some((s) => helper.hasProductOption(s, slug));

  return Object.fromEntries(
    Object.entries(FEATURE_OPTIONS).map(([flag, slug]) => [flag, anyPlanHas(slug)]),
  ) as AccountFeatures;
}

/**
 * Feature flags derived from a user's subscriptions' product options. Cross-plugin: a feature is
 * granted by any active plan carrying its option, not tied to a single plugin. Add a feature by
 * extending FEATURE_OPTIONS - the type and deriver follow automatically.
 */
export const useAccountFeatures = (): AccountFeatures => {
  const { items: subscriptions } = useSelector((state: RootState) => state.licenses);
  return useMemo(() => deriveAccountFeatures(subscriptions), [subscriptions]);
};
